import React, { useCallback } from 'react';
import { enumValue } from '~/util';
import { ServicesContext } from '../ServicesContext';
import { parsePatientIdFromUrl } from './utils/parsePatientIdFromUrl';
import { setRiskGapAnswer } from './utils/setRiskGapAnswer';
import { PatientCacheRef, PatientServiceEvents } from './usePatientService';

type GapProp = 'risk_gaps' | 'foc_gaps' | 'quality_gaps';

function getGapConfig(gapType: number): { url: string; gapProp: GapProp } | undefined {
    switch (gapType) {
        case enumValue(['gap', 'type', 'Risk Gap']):
            return { url: '/api/risk-gaps/', gapProp: 'risk_gaps' };
        case enumValue(['gap', 'type', 'FOC Gap']):
            return { url: '/api/foc-gaps/', gapProp: 'foc_gaps' };
        case enumValue(['gap', 'type', 'Quality Gap']):
            return { url: '/api/quality-gaps/', gapProp: 'quality_gaps' };
        default:
            return undefined;
    }
}

export const useGetGap = (events: PatientServiceEvents, patientCache: PatientCacheRef) => {
    const services = React.useContext(ServicesContext);

    const getGap = useCallback(
        async (gapId: number, gapType: number) => {
            const config = getGapConfig(gapType);
            if (!config) return;

            const result: ApiDataResponseType<{ patient?: string }> = await services.api.call({
                method: 'get',
                url: config.url + gapId,
            });

            const patientId = parsePatientIdFromUrl(result.data.patient);
            const patient = patientId !== undefined ? patientCache.current[patientId] : undefined;

            if (patient) {
                if (config.gapProp === 'risk_gaps') {
                    patient.risk_gaps.forEach((gap, i, arr) => {
                        if (gap.id === gapId) arr[i] = setRiskGapAnswer(result.data as RiskGapType);
                    });
                } else {
                    patient[config.gapProp].forEach((gap, i, arr) => {
                        if (gap.id === gapId) arr[i] = result.data as GapType;
                    });
                }
            }

            services.events.publish(events.patient_updated);
        },
        [services.api, services.events, events, patientCache]
    );

    return getGap;
};
